import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Clock, MessageCircle, CreditCard, Mail, Loader2, Video } from "lucide-react";
import { useSite, waLink, resolveMedia } from "../siteContext";
import { useLang, pick } from "../i18n";
import { SectionBackdrop } from "./SectionBackdrop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ease = [0.22, 1, 0.36, 1];

export const Consultations = () => {
  const settings = useSite();
  const { lang, s } = useLang();
  const ar = lang === "ar";
  const c = settings.consultation || {};
  const packages = (c.packages || []).filter((p) => p.is_active !== false);
  const [busy, setBusy] = useState("");

  const book = async (pkg) => {
    const type = pkg.booking_type || "whatsapp";
    if (type === "payment_link" && pkg.payment_link) {
      window.location.href = pkg.payment_link;
      return;
    }
    if (type === "contact") {
      document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    window.open(waLink(settings, s.consultWaMsg(pick(lang, pkg, "title")), lang), "_blank", "noopener,noreferrer");
  };

  const ctaFor = (pkg) => {
    if (pkg.booking_type === "stripe" && Number(pkg.price) > 0) return { Icon: CreditCard, label: ar ? "ادفع/ادفعي واحجز/احجزي الآن" : "Pay & book now" };
    if (pkg.booking_type === "payment_link" && pkg.payment_link) return { Icon: CreditCard, label: ar ? "ادفع/ادفعي واحجز/احجزي الآن" : "Pay & book now" };
    if (pkg.booking_type === "contact") return { Icon: Mail, label: ar ? "أرسل/أرسلي طلب حجز" : "Send a booking request" };
    return { Icon: MessageCircle, label: ar ? "احجز/احجزي عبر واتساب" : "Book via WhatsApp" };
  };

  if (!c.title && !packages.length) return null;

  return (
    <section id="consultations" data-testid="consultations-section" className="relative py-28 md:py-36 overflow-hidden">
      <SectionBackdrop id="consultations" />
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-luxury-gold/5 blur-[140px]" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
        <div className={c.image ? "grid lg:grid-cols-2 gap-14 lg:gap-20 items-center" : "text-center max-w-3xl mx-auto"}>
          {c.image ? (
          <motion.div
            initial={{ opacity: 0, x: ar ? 60 : -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease }}
            className="relative"
          >
            <div className="absolute -inset-4 border border-luxury-gold/25 rounded-3xl translate-x-4 translate-y-4" />
            <div className="relative overflow-hidden rounded-3xl border border-luxury-border">
              <img
                data-testid="consultations-image"
                src={resolveMedia(c.image)}
                alt={pick(lang, c, "title") || "Private online consultations"}
                className="w-full h-[380px] md:h-[500px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-5 inset-x-5 flex items-center gap-3 text-white bg-luxury-black/60 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-3 [text-shadow:0_1px_6px_rgba(0,0,0,0.8)]">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-luxury-gold text-luxury-black shrink-0">
                  <Video className="w-5 h-5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-white">{ar ? "جلسات مباشرة عبر الإنترنت" : "Live online sessions"}</p>
                  <p className="text-xs text-white/85">{ar ? "خصوصية وسرّية تامة — أينما كنت في العالم" : "Fully private & confidential — wherever you are"}</p>
                </div>
              </div>
            </div>
          </motion.div>
          ) : null}

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease, delay: 0.15 }}
          >
            <p className="text-luxury-gold text-sm tracking-[0.35em] mb-6">{s.consult_eyebrow}</p>
            <h2 data-testid="consultations-title" className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-luxury-bone leading-[1.25]">
              {pick(lang, c, "title")}
            </h2>
            <p data-testid="consultations-caption" className="mt-6 text-base md:text-lg text-luxury-gray leading-loose">
              {pick(lang, c, "caption")}
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-20">
          {packages.map((pkg, i) => {
            const { Icon, label } = ctaFor(pkg);
            return (
              <motion.article
                key={pkg.id}
                data-testid={`consultation-card-${i}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.1, duration: 0.8, ease }}
                className={`group relative flex flex-col bg-luxury-surface rounded-2xl p-8 border transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(212,175,55,0.12)] ${
                  pkg.is_popular ? "border-luxury-gold shadow-[0_0_40px_rgba(212,175,55,0.08)]" : "border-luxury-border hover:border-luxury-gold"
                }`}
              >
                {pkg.is_popular ? (
                  <span className="absolute -top-3.5 inset-x-0 mx-auto w-fit bg-luxury-gold text-luxury-black text-[11px] font-bold tracking-wide px-4 py-1.5 rounded-full">
                    {s.consult_popular}
                  </span>
                ) : null}
                <h3 className="font-serif text-2xl text-luxury-bone mb-3">{pick(lang, pkg, "title")}</h3>
                <p className="flex items-center gap-2 text-luxury-gray text-sm mb-6">
                  <Clock className="w-4 h-4 text-luxury-gold" strokeWidth={1.25} />
                  {pick(lang, pkg, "duration")}
                </p>
                {Number(pkg.price) > 0 ? (
                  <p data-testid={`consultation-price-${i}`} className="font-serif text-4xl text-luxury-bone mb-1">
                    {pkg.price}
                    <span className="text-sm text-luxury-gold ms-2">{(pkg.currency || "usd").toUpperCase()}</span>
                  </p>
                ) : (
                  <p data-testid={`consultation-price-${i}`} className="text-luxury-gray text-sm mb-1">{s.consult_price_tba}</p>
                )}
                <div className="flex-1" />
                <button
                  data-testid={`consultation-book-${i}`}
                  onClick={() => book(pkg)}
                  disabled={busy === pkg.id}
                  className={`mt-8 inline-flex items-center justify-center gap-2 text-sm font-semibold px-6 py-3.5 rounded-full transition-colors duration-300 disabled:opacity-60 ${
                    pkg.is_popular
                      ? "bg-luxury-gold text-luxury-black hover:bg-luxury-goldLight"
                      : "border border-luxury-gold/60 text-luxury-gold hover:bg-luxury-gold hover:text-luxury-black"
                  }`}
                >
                  {busy === pkg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" strokeWidth={1.5} />}
                  {label}
                </button>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
